import React from 'react';
import { useTranslations } from 'src/contexts/TranslationContext';
import { useNavigate } from 'react-router-dom';
import { useCanonicalTag } from '../../components/seo/useCanonicalTag';

import { PageTitleDescriptionHelmet } from 'src/components/seo/NewsPageHelmets';

interface CommonPageProps {
  lang?: string;
  pageType: 'about' | 'contact' | 'privacy' | 'terms';
  title: string;
  content: React.ReactNode;
  metaDescription?: string;
}

const CommonPage: React.FC<CommonPageProps> = ({ lang, pageType, title, content, metaDescription }) => {
  const { translations: t, language, isLoading } = useTranslations();
  const navigate = useNavigate();
  const [loadingTimeout, setLoadingTimeout] = React.useState(false);

  useCanonicalTag();

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isLoading) {
      if (t && Object.keys(t).length > 0) {
        setLoadingTimeout(true);
        return;
      }

      timeoutId = setTimeout(() => {
        setLoadingTimeout(true);
      }, 2000);
    } else {
      setLoadingTimeout(false);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isLoading, t]);

  if (isLoading && !loadingTimeout) {
    return (
      <div className="max-w-6xl mx-auto p-8 text-center">
        <h2 className="text-2xl font-semibold mb-4">Loading content...</h2>
        <p className="text-gray-500">Please wait while we load the content in {language}...</p>
      </div>
    );
  }

  const getUrlLanguagePrefix = (lang: string) => {
    return lang && lang !== 'en' ? `/${lang}` : '/en';
  };

  const getTranslatedTitle = () => {
    if (pageType === 'privacy') {
      return t?.privacy?.title || t?.navigations?.privacy?.title || title;
    }
    if (pageType === 'terms') {
      return t?.terms?.title || t?.navigations?.terms?.title || title;
    }
    if (pageType === 'about' && t?.navigations?.about?.title) {
      return t.navigations.about.title;
    }
    if (pageType === 'contact' && t?.navigations?.contact?.title) {
      return t.navigations.contact.title;
    }
    return title;
  };

  const getTranslatedType = (type: string) => {
    if (t?.navigations?.header?.[type.toLowerCase()]) {
      return t.navigations.header[type.toLowerCase()];
    }
    return type;
  };

  return (
    <>

      <PageTitleDescriptionHelmet
        title={getTranslatedTitle()}
        description={metaDescription}
      />

      <div className="max-w-6xl mx-auto">



        {/* Breadcrumb navigation */}
        <div className="px-4 pt-8">
          <div className="flex items-center mb-6 text-lg">
            <span
              onClick={() => {
                const prefix = getUrlLanguagePrefix(language);
                navigate(`${prefix}/trending`);
              }}
              className="text-blue-600 cursor-pointer hover:underline flex items-center"
            >
              {getTranslatedType('Trending')}
            </span>
            <span className="text-gray-500 mx-2">→</span>
            <span className="text-gray-500 mx-1">/</span>
            <span className="text-gray-800 font-medium">
              {getTranslatedTitle()}
            </span>
          </div>
        </div>

        <div className="container mx-auto px-4 py-4">
          <h1 className="text-3xl font-bold mb-6">{getTranslatedTitle()}</h1>
          {content}
        </div>
      </div>
    </>
  );
};

export default CommonPage;
